// Central state singleton — replaces the legacy scattered file-scope globals
// (`canvas`, `currentSize`, `orientation`, `batchData`, undo/redo stacks, etc.).
//
// This is the #1 untangling fix from docs/olm-extraction-map.md §3.3: the legacy
// `canvas` global is read/written by ~every domain and `initCanvas` wires ~14
// cross-domain callbacks into it. Here, modules import this store instead of
// reaching into a file-scope binding, which avoids the circular-import hub.

import type { Canvas, FabricObject } from 'fabric'
import type { User } from '@supabase/supabase-js'
import type { BatchRow, LabelSize, Orientation, SheetLayout } from './types'
import { LABEL_SIZES } from './constants'
import { PRODUCT } from './product'

export type Tier = 'free' | 'pro' | 'business'

export interface EditorState {
  /** The single Fabric canvas instance. Set once by canvas.ts initCanvas(). */
  canvas: Canvas | null

  currentSize: LabelSize
  /** Selected multi-up sheet layout, or null for single-label/printer mode. */
  currentSheet: SheetLayout | null
  orientation: Orientation
  zoomFactor: number

  selectedObject: FabricObject | null

  // Batch (legacy batchData / batchHeaders / batchFields, map §3.1)
  batchData: BatchRow[]
  batchHeaders: string[]
  batchFields: string[]
  batchLivePreviewActive: boolean

  // History (legacy undoStack / redoStack / isUndoRedo)
  undoStack: string[]
  redoStack: string[]
  isUndoRedo: boolean

  // Grid (legacy gridEnabled / snapToGrid / gridSizeMM)
  gridEnabled: boolean
  snapToGrid: boolean
  gridSize: number
  // Label shape (legacy _roundedCornersEnabled)
  roundedCorners: boolean

  // Cloud identity (legacy currentDesignId / currentDesignName)
  currentDesignId: string | null
  currentDesignName: string

  // Auth (legacy currentUser / _olmOrgTier)
  currentUser: User | null
  orgTier: 'business' | null
}

function initialSize(): LabelSize {
  return LABEL_SIZES.find((s) => s.id === PRODUCT.defaultSizeId) ?? LABEL_SIZES[0]
}

export const state: EditorState = {
  canvas: null,
  currentSize: initialSize(),
  currentSheet: null,
  orientation: PRODUCT.defaultOrientation,
  zoomFactor: 1,
  selectedObject: null,
  batchData: [],
  batchHeaders: [],
  batchFields: [],
  batchLivePreviewActive: false,
  undoStack: [],
  redoStack: [],
  isUndoRedo: false,
  gridEnabled: false,
  snapToGrid: false,
  gridSize: 5,
  roundedCorners: false,
  currentDesignId: null,
  currentDesignName: 'Untitled',
  currentUser: null,
  orgTier: null,
}

/** Accessor used by modules that must not capture a stale `canvas` binding. */
export function getCanvas(): Canvas {
  if (!state.canvas) throw new Error('Canvas not initialised — call initCanvas() first')
  return state.canvas
}
